import { fieldVisible, type Me } from '../auth/me';
import { warsawParts } from '../events/labels';
import { parsePln, plnToInput } from '../catalog/money';

/** Company tasks (P7): the server's resource, query key and shapes. */
export const TASKS_RESOURCE = 'company_tasks.tasks';
export const TASKS_KEY = ['company-tasks'] as const;

export type Repeat = 'weekly' | 'monthly';
export const REPEAT_LABELS: Record<Repeat, string> = { weekly: 'Co tydzień', monthly: 'Co miesiąc' };

/** A task as the server sends it; fields hidden from the role are absent (rule 7a). */
export type Task = {
  id: string;
  title: string;
  description: string;
  dueDay: string;
  dueTime: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  repeat: Repeat | null;
  done: boolean;
  doneAt: string | null;
  doneByName: string | null;
  doneNote: string;
  /** "Przyjmuję" of the person responsible (absent when "Wykonane" is hidden); null: not yet. */
  acceptedAt?: string | null;
  /** Owner 2026-10-22 01:34: net cost in grosze (sensitive: absent for a role without it). */
  costGrosze?: number | null;
  costCategory?: string;
  version: number;
};
export type Assignee = { id: string; name: string };

export const isOverdue = (t: Pick<Task, 'done' | 'dueDay'>, today: string): boolean => !t.done && !!t.dueDay && t.dueDay < today;

const dayText = (day: string) => {
  const [y, m, d] = day.split('-');
  return `${d}.${m}.${y}`;
};
export const formatDue = (t: Pick<Task, 'dueDay' | 'dueTime'>): string => (t.dueDay ? `${dayText(t.dueDay)}${t.dueTime ? `, ${t.dueTime}` : ''}` : '—');

/** "Do zrobienia", or "Zrobione 11.11 · Ania" (the Warsaw day it was ticked off). */
export function statusText(t: Pick<Task, 'done' | 'doneAt' | 'doneByName'>): string {
  if (!t.done) return 'Do zrobienia';
  const day = t.doneAt ? warsawParts(t.doneAt).day : null;
  return ['Zrobione' + (day ? ` ${day.slice(8, 10)}.${day.slice(5, 7)}` : ''), t.doneByName].filter(Boolean).join(' · ');
}

/** The scope of `action` on the tasks for this user (null: not allowed). */
export function scopeOf(me: Me, action: string): 'all' | 'own' | null {
  if (me.isOwner) return 'all';
  return me.permissions.find((p) => p.resource === TASKS_RESOURCE && p.action === action)?.scope ?? null;
}

/**
 * Whether `me` may tick `t` off: the person responsible always (owner decision: the task is theirs, with or without a
 * grant), anybody else with "edit" for all tasks; never when the role does not see "Wykonane" (rule 7a).
 */
export function canTickOff(me: Me, t: Pick<Task, 'assigneeUserId'>): boolean {
  if (!fieldVisible(me, TASKS_RESOURCE, 'done')) return false;
  return t.assigneeUserId === me.id || scopeOf(me, 'edit') === 'all';
}

/** The edit form's values (text inputs: empty string for "none"). */
export type Draft = {
  title: string;
  description: string;
  dueDay: string;
  dueTime: string;
  assigneeUserId: string;
  repeat: '' | Repeat;
  done: boolean;
  doneNote: string;
  /** Złoty as typed ("1 234,50"); empty: no cost. */
  cost: string;
  costCategory: string;
};

/** The cost field's grosze: null when empty, undefined when it is not a valid amount. */
function costOf(text: string): number | null | undefined {
  if (text.trim() === '') return null;
  try {
    return parsePln(text);
  } catch {
    return undefined;
  }
}
/** The message for a cost that is not an amount (null when fine or empty). */
export function costError(text: string): string | null {
  return costOf(text) === undefined ? 'Podaj kwotę w złotych, np. 1 234,50' : null;
}

export const draftOf = (t: Task): Draft => ({
  title: t.title ?? '',
  description: t.description ?? '',
  dueDay: t.dueDay ?? '',
  dueTime: t.dueTime ?? '',
  assigneeUserId: t.assigneeUserId ?? '',
  repeat: t.repeat ?? '',
  done: t.done ?? false,
  doneNote: t.doneNote ?? '',
  cost: t.costGrosze === null || t.costGrosze === undefined ? '' : plnToInput(t.costGrosze),
  costCategory: t.costCategory ?? '',
});

/** The fields of `draft` that differ from `task`, as the server takes them, and only those the role may see (rule 7a). */
export function changedBody(task: Task, draft: Draft, visible: (field: string) => boolean): Record<string, unknown> {
  const before = draftOf(task);
  const out: Record<string, unknown> = {};
  const put = (key: string, value: unknown) => {
    if (visible(key)) out[key] = value;
  };
  if (draft.title.trim() !== before.title) put('title', draft.title.trim());
  if (draft.description.trim() !== before.description) put('description', draft.description.trim());
  if (draft.dueDay !== before.dueDay) put('dueDay', draft.dueDay);
  if (draft.dueTime !== before.dueTime) put('dueTime', draft.dueTime || null);
  if (draft.assigneeUserId !== before.assigneeUserId) put('assigneeUserId', draft.assigneeUserId || null);
  if (draft.repeat !== before.repeat) put('repeat', draft.repeat || null);
  if (draft.done !== before.done) put('done', draft.done);
  if (draft.doneNote.trim() !== before.doneNote) put('doneNote', draft.doneNote.trim());
  const cost = costOf(draft.cost);
  if (cost !== undefined && cost !== (task.costGrosze ?? null)) put('costGrosze', cost);
  if (draft.costCategory.trim() !== before.costCategory) put('costCategory', draft.costCategory.trim());
  return out;
}

// ---------- the calendar layer ----------

const layerKey = (userId: string) => `mek.calendar.companyTasks.${userId}`;

/** Whether the calendar shows the company tasks (a per-user, per-browser setting; off when storage is missing). */
export function loadTaskLayer(userId: string): boolean {
  try {
    return window.localStorage.getItem(layerKey(userId)) === '1';
  } catch {
    return false;
  }
}
export function saveTaskLayer(userId: string, on: boolean): void {
  try {
    window.localStorage.setItem(layerKey(userId), on ? '1' : '0');
  } catch {
    /* storage unavailable: just not remembered */
  }
}
